/**
 * `/styleguide/verification` — design archive of the verification UI/UX prototype.
 *
 * WP1b / #46598 (HG1): the production verification shell is `/`. The bare
 * styleguide URL redirects there so bookmarks land on the product. Deeper
 * prototype pages (`/styleguide/verification/queue`, …) stay mounted as a
 * clearly marked non-product design archive (WP10a owns the exact-path redirect
 * cluster; splat stays archive). Prototype page files themselves are not edited here (WP1c / WP6).
 *
 * A PROTOTYPE, deliberately: it proposes a whole information architecture for
 * Excellent-as-verification-system (sidebar, page hierarchy, object pages) rather
 * than extending the shipped chrome. It renders on a bare canvas
 * (`prototypes/bare-canvas.ts`) so the rails it is proposing to replace are not
 * also on screen arguing with it.
 *
 * ── Why two routes and not ten ──
 *
 * Every prototype page is addressable — `/styleguide/verification/queue`,
 * `…/runs/run_8814`, `…/guide` — because a design you cannot deep-link into is a
 * design you cannot review in a comment thread. But registering ten TanStack
 * routes would put this prototype's page list in `tree.ts`, where it would read
 * as product IA. So the tree gets exactly two entries (index + splat) and the
 * prototype resolves its own path in `prototypes/verification/router.ts`.
 *
 * ── Data ──
 *
 * The prototype runs on a fixture workspace (`prototypes/verification/data.ts`)
 * whose shapes are taken from the REAL domain model in
 * `packages/core/src/verification/product-model.ts` — same field names, same
 * status literals. That is the point of a prototype at this stage: every screen
 * is populated and every interaction resolves, so the review is about the design
 * and not about which endpoint is mounted yet. Where a screen shows a number the
 * live product computes, the fixture carries a plausible value, and the guide
 * (`…/guide`) marks which capabilities are built vs. planned in the real product.
 */

import { createRoute, redirect } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { VerificationPrototype } from "../prototypes/verification/app";
import { resolveRootStyleguideRedirect } from "./verification-legacy-redirects";

/** Bare `/styleguide/verification` → production `/` (HG1 / WP10a). Query is forwarded. */
export const styleguideVerificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/styleguide/verification",
  beforeLoad: ({ location }) => {
    const dest = resolveRootStyleguideRedirect(location.pathname, location.searchStr) ?? "/";
    throw redirect({ to: dest, replace: true });
  },
});

/**
 * The splat sibling, which catches every deeper prototype page.
 *
 * Empty splat is rejected so a hard-load of `/styleguide/verification` hits the
 * exact route (and therefore the redirect to `/`) instead of this wildcard.
 * Remaining splat URLs are the non-product design archive (HG1).
 */
function StyleguideVerificationArchive() {
  return (
    <div data-design-archive="styleguide-verification">
      <p className="border-b px-4 py-2 type-body" role="note">
        Design archive — not the product. Production verification lives at{" "}
        <a href="/">/</a>.
      </p>
      <VerificationPrototype />
    </div>
  );
}

export const styleguideVerificationSplatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/styleguide/verification/$",
  // #44601/#46444: empty splat outranks the bare route. Reject it so a hard-load
  // of `/styleguide/verification` mounts the exact route, not the wildcard.
  params: {
    parse: (raw) => {
      if (!raw._splat) return false;
      return raw;
    },
  },
  component: StyleguideVerificationArchive,
});
